import type {
  MealPlanExportDay,
  MealPlanExportForPdfData,
  MealPlanExportItem,
  MealPlanExportMeal,
  MealPlanExportRecipeDetails,
  MealPlanExportRecipeIngredient,
} from '@/types/meal-plan';

export type MealPlanExportIngredientRow = {
  name: string;
  quantity: string;
};

export type MealPlanExportItemDetails = {
  ingredients: MealPlanExportIngredientRow[];
  instructions: string[];
};

export type MealPlanExportItemDetailsMap = Map<string, MealPlanExportItemDetails>;

export function unwrapMealPlanExportData(raw: unknown): MealPlanExportForPdfData {
  if (!raw || typeof raw !== 'object') return {} as MealPlanExportForPdfData;
  const record = raw as Record<string, unknown>;
  if (record.data && typeof record.data === 'object' && !Array.isArray(record.data)) {
    return record.data as MealPlanExportForPdfData;
  }
  return record as MealPlanExportForPdfData;
}

function collectGenericExportMeals(data: MealPlanExportForPdfData): MealPlanExportMeal[] {
  const fromDays = (Array.isArray(data.days) ? data.days : []).flatMap((day) =>
    Array.isArray(day.meals) ? day.meals : [],
  );
  if (fromDays.length > 0) {
    return fromDays;
  }

  if (Array.isArray(data.mealSlots)) {
    return data.mealSlots as MealPlanExportMeal[];
  }

  if (Array.isArray(data.meals)) {
    return data.meals as MealPlanExportMeal[];
  }

  return [];
}

/** Flatten generic export payloads into a single day for the PDF pipeline (no day structure). */
export function normalizeGenericMealPlanExportForPdf(raw: unknown): unknown {
  const envelope = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  const data = unwrapMealPlanExportData(raw);
  const allMeals = collectGenericExportMeals(data);
  if (allMeals.length === 0) {
    return raw;
  }

  const normalizedData: MealPlanExportForPdfData = {
    ...data,
    durationDays: 1,
    days: [
      {
        id: `${String(data.id ?? 'generic')}-meals`,
        dayNumber: 1,
        dayName: null,
        notes: null,
        meals: allMeals,
      } satisfies MealPlanExportDay,
    ],
  };

  if (envelope?.data && typeof envelope.data === 'object' && !Array.isArray(envelope.data)) {
    return { ...envelope, data: normalizedData };
  }

  return normalizedData;
}

export function formatQty(value: number): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  const intPart = Math.floor(num);
  const decimalPart = num - intPart;
  if (Math.abs(decimalPart - 0.25) < 0.01) return intPart > 0 ? `${intPart}¼` : '¼';
  if (Math.abs(decimalPart - 0.33) < 0.02) return intPart > 0 ? `${intPart}⅓` : '⅓';
  if (Math.abs(decimalPart - 0.5) < 0.01) return intPart > 0 ? `${intPart}½` : '½';
  if (Math.abs(decimalPart - 0.67) < 0.02) return intPart > 0 ? `${intPart}⅔` : '⅔';
  if (Math.abs(decimalPart - 0.75) < 0.01) return intPart > 0 ? `${intPart}¾` : '¾';
  if (decimalPart < 0.01) return String(intPart);
  return num.toFixed(1).replace(/\.0$/, '');
}

/** Human-readable amount + unit (e.g. "1 pinch", "½ cup", "400 g"). */
export function formatIngredientAmountAndUnit(
  amount: number | undefined,
  unitRaw?: string | null,
): string {
  const unit = String(unitRaw ?? '').trim();
  if (amount == null || !Number.isFinite(Number(amount))) return unit;
  const amountLabel = formatQty(Number(amount));
  if (!amountLabel) return unit;
  return [amountLabel, unit].filter(Boolean).join(' ').trim();
}

export function formatExportIngredientQuantity(
  ingredient: MealPlanExportRecipeIngredient,
): string {
  const record = ingredient as MealPlanExportRecipeIngredient & {
    unit?: string;
    servingSize?: string;
    amountGrams?: number;
  };
  let unit = record.servingId ?? record.unit ?? record.servingSize;
  if (!unit && record.amountGrams != null && record.amountGrams > 0) {
    unit = 'g';
  }
  const amount =
    ingredient.amount ??
    (unit === 'g' && record.amountGrams != null && record.amountGrams > 0
      ? record.amountGrams
      : undefined);
  return formatIngredientAmountAndUnit(amount, unit);
}

export function parseExportRecipeDetails(
  recipeDetails: MealPlanExportRecipeDetails | null | undefined,
): MealPlanExportItemDetails | null {
  if (!recipeDetails || typeof recipeDetails !== 'object') return null;

  const ingredients = Array.isArray(recipeDetails.ingredients)
    ? recipeDetails.ingredients
        .map((ingredient) => {
          const name = String(ingredient.name ?? '').trim();
          if (!name) return null;
          return {
            name,
            quantity: formatExportIngredientQuantity(ingredient),
          };
        })
        .filter((row): row is MealPlanExportIngredientRow => row !== null)
    : [];

  const instructions = Array.isArray(recipeDetails.instructions)
    ? recipeDetails.instructions
        .map((step) => String(step.instruction ?? '').trim())
        .filter((text) => text.length > 0)
    : [];

  if (ingredients.length === 0 && instructions.length === 0) return null;
  return { ingredients, instructions };
}

export function buildExportItemDetailsMap(
  data: MealPlanExportForPdfData,
): MealPlanExportItemDetailsMap {
  const map: MealPlanExportItemDetailsMap = new Map();

  for (const day of data.days ?? []) {
    const meals = Array.isArray(day.meals) ? day.meals : [];
    for (const meal of meals) {
      for (const item of meal.items ?? []) {
        const details = parseExportRecipeDetails(item.recipeDetails);
        if (!details) continue;
        const keys = [item.itemId, item.id].map((value) => String(value ?? '').trim()).filter(Boolean);
        keys.forEach((key) => map.set(key, details));
      }
    }
  }

  return map;
}

export function lookupExportItemDetails(
  map: MealPlanExportItemDetailsMap | undefined,
  itemId?: string | null,
  fallbackId?: string | null,
): MealPlanExportItemDetails | null {
  if (!map || map.size === 0) return null;
  for (const key of [itemId, fallbackId]) {
    const trimmed = String(key ?? '').trim();
    if (!trimmed) continue;
    const found = map.get(trimmed);
    if (found) return found;
  }
  return null;
}

/** Flatten export item `recipeDetails` onto fields used by PDF transform. */
export function flattenExportItemRecord(item: MealPlanExportItem | Record<string, unknown>): Record<string, unknown> {
  const record = item as Record<string, unknown>;
  const recipeDetails = record.recipeDetails as MealPlanExportRecipeDetails | null | undefined;
  if (!recipeDetails) return record;

  const parsed = parseExportRecipeDetails(recipeDetails);
  return {
    ...record,
    recipeName: record.recipeName ?? record.name,
    ingredients: recipeDetails.ingredients,
    instructions: recipeDetails.instructions,
    ingredientLines: parsed?.ingredients.map((row) =>
      [row.quantity, row.name].filter(Boolean).join(' ').trim(),
    ),
  };
}
